import { PolfEventRankingEnum } from '@polf/shared/codegen/types'
import { useState } from 'react'

import type {
  GolfLeaderboardPlayerEntryFragment,
  LeaderboardUnionFieldsFragment,
} from '../../__generated__/hooks'
import GolfDetail from './GolfDetail'
import GolfRow from './GolfRow'
import Header, { scoringNote } from './Header'
import Hero from './Hero'
import type { CourseHoleSummary } from './MiniScorecard'

type GolfLb = Extract<
  LeaderboardUnionFieldsFragment,
  { __typename: 'GolfLeaderboard' }
>

/**
 * Plain Golf event — individual stableford or net-strokes ranking, no
 * format-level toggle. The Hero copy follows the ranking. Tapping a
 * row opens the GolfDetail sheet with summary stats + scorecard.
 */
const GolfView = ({
  lb,
  youId,
  ranking,
  courseHoles,
}: {
  lb: GolfLb
  youId?: string
  ranking?: PolfEventRankingEnum | null
  courseHoles: readonly CourseHoleSummary[]
}) => {
  const isNet = ranking === PolfEventRankingEnum.NetStrokes
  const [selected, setSelected] =
    useState<GolfLeaderboardPlayerEntryFragment | null>(null)
  const players = lb.players ?? []
  const you = youId ? players.find((p) => p.player.id === youId) ?? null : null
  return (
    <section className="pb-4">
      <Header scoring={scoringNote(ranking)} />
      {you ? (
        <Hero
          yourRank={you.rank ?? null}
          yourPrimary={String(
            isNet ? you.strokesNet || you.strokes : you.points
          )}
          primarySuffix={isNet ? 'slag' : 'pts'}
          leaderGap={null}
          totalPlayers={players.length}
        />
      ) : null}
      <ul className="bg-paper mx-2 rounded-lg shadow-sm overflow-hidden">
        {players.map((entry) => (
          <GolfRow
            key={entry.player.id}
            entry={entry}
            isYou={youId != null && entry.player.id === youId}
            isNet={isNet}
            onClick={() => setSelected(entry)}
          />
        ))}
      </ul>
      <GolfDetail
        open={selected != null}
        entry={selected}
        isNet={isNet}
        courseHoles={courseHoles}
        onClose={() => setSelected(null)}
      />
    </section>
  )
}

export default GolfView
